import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BsTrophy, BsFillStarFill, BsGraphUp } from 'react-icons/bs';
import AppHeader from '../components/layout/AppHeader';
import { apiClient } from '../services/api';

const Leaderboard = () => {
  const [activeTab, setActiveTab] = useState('squads');
  const [impactMetrics, setImpactMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Mock data for MVP purposes until backend fully populates
  const topSquads = [
    { rank: 1, name: "Kasi Innovators", points: 12450, completed: 42, focus: "Web3/DeFi" },
    { rank: 2, name: "Nairobi Frontend Elite", points: 11200, completed: 38, focus: "React/Next.js" },
    { rank: 3, name: "Lagos Backend Guild", points: 9800, completed: 31, focus: "Node/Go" },
    { rank: 4, name: "Cape Town UI Masters", points: 8400, completed: 25, focus: "Design/UX" },
    { rank: 5, name: "Harare Cloud Architects", points: 7950, completed: 19, focus: "AWS/DevOps" }
  ];

  const risingTalents = [
    { rank: 1, name: "Samuel T.", role: "Fullstack Dev", streak: "14 Weeks", growth: "+45%" },
    { rank: 2, name: "Aisha M.", role: "Smart Contract Eng", streak: "11 Weeks", growth: "+38%" },
    { rank: 3, name: "David B.", role: "UI/UX Designer", streak: "9 Weeks", growth: "+32%" },
    { rank: 4, name: "Grace O.", role: "Rust Developer", streak: "8 Weeks", growth: "+29%" },
  ];

  useEffect(() => {
    const fetchImpact = async () => {
      try {
        setLoading(true);
        // We reuse the getPlatformImpactMetrics endpoint that we created earlier
        const res = await apiClient.collaborationServiceRequest('/dashboard/platform-impact');
        setImpactMetrics(res.impact);
      } catch (err) {
        console.error("Failed to load impact metrics", err);
      } finally {
        setLoading(false);
      }
    };
    fetchImpact();
  }, []);

  return (
    <div className="min-h-screen bg-premium-wood-dark font-sans text-white overflow-x-hidden relative">
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/60 via-premium-wood-dark/80 to-black/90 pointer-events-none" />
      
      {/* Header */}
      <AppHeader />

      <main className="relative z-10 container mx-auto px-4 pt-12 pb-24 max-w-6xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="text-center mb-12">
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-b from-premium-gold-light via-premium-gold to-premium-gold-dark drop-shadow-lg mb-4 flex items-center justify-center gap-4">
              <BsTrophy className="text-premium-gold" /> Global Leaderboards
            </h1>
            <p className="text-lg text-white/60 max-w-2xl mx-auto">
              Celebrating the top-performing Virtual Squads and the fastest-rising talent across the TechUnity network.
            </p>
          </div>

          {/* Quick Platform Impact Highlight */}
          {impactMetrics && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
               <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                  <div className="text-premium-gold font-bold text-2xl mb-1">${(impactMetrics.financials?.successfullyPaidOutUSD || 0).toLocaleString()}</div>
                  <div className="text-xs text-white/50 uppercase tracking-wider">Total Earned</div>
               </div>
               <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                  <div className="text-premium-gold font-bold text-2xl mb-1">{impactMetrics.activity?.historicallyCompletedTasks || 0}</div>
                  <div className="text-xs text-white/50 uppercase tracking-wider">Tasks Completed</div>
               </div>
               <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                  <div className="text-premium-gold font-bold text-2xl mb-1">{impactMetrics.activity?.activeVirtualSquads || 0}</div>
                  <div className="text-xs text-white/50 uppercase tracking-wider">Active Squads</div>
               </div>
               <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                  <div className="text-premium-gold font-bold text-2xl mb-1">{impactMetrics.trust?.globalAverageTrustScore || 50}/100</div>
                  <div className="text-xs text-white/50 uppercase tracking-wider">Global Trust Score</div>
               </div>
            </div>
          )}

          {/* Leaderboard Tabs */}
          <div className="flex justify-center gap-4 mb-8">
            <button
              onClick={() => setActiveTab('squads')}
              className={`px-8 py-3 rounded-full font-bold transition-all ${
                activeTab === 'squads' 
                ? 'bg-premium-gold text-premium-wood-dark shadow-[0_0_20px_rgba(212,175,55,0.4)]' 
                : 'bg-white/10 hover:bg-white/20 text-white'
              }`}
            >
              Top Squads
            </button>
            <button
              onClick={() => setActiveTab('talent')}
              className={`px-8 py-3 rounded-full font-bold transition-all ${
                activeTab === 'talent' 
                ? 'bg-primary-blue text-white shadow-[0_0_20px_rgba(66,133,244,0.4)]' 
                : 'bg-white/10 hover:bg-white/20 text-white'
              }`}
            >
              Rising Talent
            </button>
          </div>

          {/* Leaderboard Content */}
          <div className="bg-black/40 border border-white/10 rounded-2xl overflow-hidden backdrop-blur-md">
            {activeTab === 'squads' && (
              <table className="w-full text-left">
                <thead className="bg-white/5 border-b border-white/10">
                  <tr>
                    <th className="p-4 font-normal text-white/50 text-sm w-16 text-center">Rank</th>
                    <th className="p-4 font-normal text-white/50 text-sm">Squad Name</th>
                    <th className="p-4 font-normal text-white/50 text-sm hidden md:table-cell">Focus Area</th>
                    <th className="p-4 font-normal text-white/50 text-sm text-center">Tasks Completed</th>
                    <th className="p-4 font-normal text-white/50 text-sm text-right">Reputation Points</th>
                  </tr>
                </thead>
                <tbody>
                  {topSquads.map((squad, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="p-4 text-center">
                        {squad.rank === 1 && <span className="inline-block w-8 h-8 bg-yellow-400 text-black rounded-full leading-8 font-bold">1</span>}
                        {squad.rank === 2 && <span className="inline-block w-8 h-8 bg-gray-300 text-black rounded-full leading-8 font-bold">2</span>}
                        {squad.rank === 3 && <span className="inline-block w-8 h-8 bg-orange-400 text-black rounded-full leading-8 font-bold">3</span>}
                        {squad.rank > 3 && <span className="font-bold text-white/50">{squad.rank}</span>}
                      </td>
                      <td className="p-4 font-bold text-white flex items-center gap-3">
                        <div className="w-8 h-8 rounded shrink-0 bg-gradient-to-br from-premium-gold/40 to-black border border-premium-gold/50 flex items-center justify-center text-xs">
                          {squad.name.charAt(0)}
                        </div>
                        {squad.name}
                      </td>
                      <td className="p-4 text-sm text-white/70 hidden md:table-cell">{squad.focus}</td>
                      <td className="p-4 text-center font-mono text-premium-gold">{squad.completed}</td>
                      <td className="p-4 text-right font-bold flex justify-end items-center gap-2">
                        {squad.points.toLocaleString()} <BsFillStarFill className="text-premium-gold text-xs" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {activeTab === 'talent' && (
              <table className="w-full text-left">
                <thead className="bg-white/5 border-b border-white/10">
                  <tr>
                    <th className="p-4 font-normal text-white/50 text-sm w-16 text-center">Rank</th>
                    <th className="p-4 font-normal text-white/50 text-sm">Talent Name</th>
                    <th className="p-4 font-normal text-white/50 text-sm hidden md:table-cell">Primary Role</th>
                    <th className="p-4 font-normal text-white/50 text-sm text-center">Delivery Streak</th>
                    <th className="p-4 font-normal text-white/50 text-sm text-right">Growth Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {risingTalents.map((talent, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="p-4 text-center">
                         <span className="font-bold text-white/50">{talent.rank}</span>
                      </td>
                      <td className="p-4 font-bold text-white flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full shrink-0 bg-primary-blue/30 border border-primary-blue/50 flex items-center justify-center text-xs">
                          {talent.name.charAt(0)}
                        </div>
                        {talent.name}
                      </td>
                      <td className="p-4 text-sm text-white/70 hidden md:table-cell">{talent.role}</td>
                      <td className="p-4 text-center font-mono text-orange-400">🔥 {talent.streak}</td>
                      <td className="p-4 text-right font-bold text-green-400 flex justify-end items-center gap-2">
                        {talent.growth} <BsGraphUp />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            
            <div className="p-4 text-center text-xs text-white/40 bg-white/5">
              Leaderboards updated dynamically based on successfully delivered projects and impact score recalculations.
            </div>
          </div>

        </motion.div>
      </main>
    </div>
  );
};

export default Leaderboard;
